import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { selectData, supabase } from "../lib/supabase";

export default function AdminInvoicesList() {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadInvoices();
  }, []);

  async function updateStatus(invoiceId, newStatus) {
    try {
      const { error } = await supabase
        .from("invoices")
        .update({ status: newStatus })
        .eq("id", invoiceId);

      if (error) throw error;

      setInvoices((prev) =>
        prev.map((inv) =>
          inv.id === invoiceId ? { ...inv, status: newStatus } : inv
        )
      );
    } catch (error) {
      console.error(error);
      alert("Erreur lors de la mise à jour du statut");
    }
  }

  function getStatusColor(status) {
    if (status === "paid") return "#22c55e";
    if (status === "sent") return "#3b82f6";
    if (status === "cancelled") return "#ef4444";
    return "#facc15";
  }

  function getStatusLabel(status) {
    if (status === "paid") return "Payée";
    if (status === "sent") return "Envoyée";
    if (status === "cancelled") return "Annulée";
    return "Brouillon";
  }

  async function loadInvoices() {
    try {
      setLoading(true);
      setMessage("");

      const result = await selectData("invoices", {
        order: { column: "created_at", ascending: false },
      });

      if (result.error) throw result.error;
      setInvoices(result.data || []);
    } catch (error) {
      console.error(error);
      setMessage("Impossible de charger les factures.");
    } finally {
      setLoading(false);
    }
  }

  function formatMoney(value, currency = "EUR") {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency,
    }).format(Number(value || 0));
  }

  function formatDate(value) {
    if (!value) return "-";
    return new Date(value).toLocaleDateString("fr-FR");
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.topbar}>
          <div>
            <h1 style={styles.title}>Mes factures</h1>
            <p style={styles.subtitle}>
              Consulte, ouvre et mets à jour manuellement le statut de tes factures.
            </p>
          </div>

          <div style={styles.actions}>
            <Link to="/admin" style={styles.secondaryButton}>
              Dashboard
            </Link>
            <Link to="/admin/factures/nouvelle" style={styles.primaryButton}>
              Nouvelle facture
            </Link>
          </div>
        </div>

        {message ? <div style={styles.alert}>{message}</div> : null}

        <div style={styles.card}>
          {loading ? (
            <p>Chargement...</p>
          ) : invoices.length === 0 ? (
            <p>Aucune facture trouvée.</p>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Numéro</th>
                    <th style={styles.th}>Client</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Total</th>
                    <th style={styles.th}>Statut</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td style={styles.td}>{invoice.invoice_number}</td>
                      <td style={styles.td}>{invoice.recipient_name}</td>
                      <td style={styles.td}>{formatDate(invoice.issue_date)}</td>
                      <td style={styles.td}>
                        {formatMoney(invoice.total_amount, invoice.currency)}
                      </td>

                      <td style={styles.td}>
                        <div style={styles.statusCell}>
                          <span
                            style={{
                              ...styles.statusBadge,
                              backgroundColor: getStatusColor(invoice.status),
                              color: invoice.status === "draft" ? "#111827" : "#fff",
                            }}
                          >
                            {getStatusLabel(invoice.status)}
                          </span>

                          <select
                            value={invoice.status || "draft"}
                            onChange={(e) => updateStatus(invoice.id, e.target.value)}
                            style={styles.statusSelect}
                          >
                            <option value="draft">Brouillon</option>
                            <option value="sent">Envoyée</option>
                            <option value="paid">Payée</option>
                            <option value="cancelled">Annulée</option>
                          </select>
                        </div>
                      </td>

                      <td style={styles.td}>
                        <Link
                          to={`/admin/factures/${invoice.id}`}
                          style={styles.linkButton}
                        >
                          Ouvrir
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    background: "#0b1020",
    minHeight: "100vh",
    padding: "40px 20px",
    color: "#fff",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  topbar: {
    display: "flex",
    justifyContent: "space-between",
    gap: "20px",
    flexWrap: "wrap",
    alignItems: "flex-start",
    marginBottom: "24px",
  },
  title: {
    fontSize: "2rem",
    fontWeight: 800,
    marginBottom: "8px",
  },
  subtitle: {
    color: "rgba(255,255,255,0.72)",
  },
  actions: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  card: {
    background: "#121933",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "18px",
    padding: "20px",
  },
  tableWrap: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "14px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    color: "#9fb1ff",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "14px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    verticalAlign: "middle",
  },
  statusCell: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: "220px",
  },
  statusBadge: {
    padding: "6px 10px",
    borderRadius: "999px",
    fontWeight: 700,
    fontSize: "0.85rem",
    minWidth: "90px",
    textAlign: "center",
  },
  statusSelect: {
    padding: "6px 10px",
    borderRadius: "8px",
    background: "#0d1430",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.2)",
    outline: "none",
    fontSize: "0.85rem",
  },
  primaryButton: {
    padding: "12px 16px",
    borderRadius: "10px",
    background: "#4f7cff",
    color: "#fff",
    textDecoration: "none",
    fontWeight: 700,
  },
  secondaryButton: {
    padding: "12px 16px",
    borderRadius: "10px",
    background: "#1a2347",
    color: "#fff",
    textDecoration: "none",
    fontWeight: 600,
    border: "1px solid rgba(255,255,255,0.12)",
  },
  linkButton: {
    padding: "8px 12px",
    borderRadius: "8px",
    background: "#4454ff",
    color: "#fff",
    textDecoration: "none",
    fontWeight: 600,
    display: "inline-block",
  },
  alert: {
    padding: "12px 14px",
    borderRadius: "12px",
    background: "rgba(255, 87, 87, 0.12)",
    border: "1px solid rgba(255, 87, 87, 0.28)",
    marginBottom: "18px",
  },
};